/* eslint-disable custom-rules/modal-and-drawer-requires-title */
import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { Button } from '@uhg-abyss/web/ui/Button';
import { CollapseProvider } from '@uhg-abyss/web/ui/CollapseProvider';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { getFeatureFlags } from '../../../common/Utils';
import { useLagoon } from '../../../hooks/useLagoon/useLagoon';
import { useNotAvailableUnknownText } from '../../../hooks/useNotAvailableUnknownText';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { FacilityLocationsResponse } from '../../../models/FacilityDetails';
import {
  PhoneNumbers,
  ProviderDetails,
  ProviderLocation,
  ProviderType,
} from '../../../models/ProviderDetails';
import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import {
  getAdditionalPhone,
  getFormattedPhoneNumber,
  getPrimaryOrAfterHoursPhone,
  getPrimaryPhone,
} from '../../../utils/phone.utils';
import {
  getCurrentMember,
  getMemberCoverageTypeSitusState,
} from '../../../utils/user.utils';
import { ReverseCoverageTypesCodes } from '../../Constants';
import { h2SmallerFontForResponsive, phoneOnly } from '../../ConstantsStyles';
import { AfterHoursPopoverIcon } from './AfterHoursPopoverIcon';
import { EmailAndWebsiteInfo } from './AllOtherInfos/EmailAndWebsiteInfo';
import { FaxSection } from './FaxSection';
import {
  ArrowContainer,
  CollapsePhonesContainer,
  CollapsePhonesHeader,
  IconAlignmentContainer,
  PhonesContainer,
  SectionHeader,
} from './ProviderLocation.style';
import {
  CollapsePhoneContainer,
  CollapsePhoneNumberContainer,
  IconSymbolContainer,
  PhoneAllContainer,
  infoSubTextContainer,
} from './ProviderLocationsDetails.style';
import { ViewAllPhonesLink } from './ViewAllPhonesLink';

export type Props = {
  selectedLocation?: ProviderLocation | FacilityLocationsResponse;

  collapseHeader: string;
  phoneNumbers?: PhoneNumbers | null;
  providerDetails?: ProviderDetails;
  tty: string;
};

type AfterHoursInfoDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  content?: string;
};

type AfterHoursLabelProps = {
  label: string;
  headingStyle: React.CSSProperties;
  shouldShowInfoIcon: boolean;
  phoneScreen: boolean;
  isWidget: boolean;
  onIconClick: () => void;
  popoverContent: string;
  isPopoverOpen: boolean;
  setIsPopoverOpen: (open: boolean) => void;
};

type PhoneEntryProps = {
  label: string;
  phoneNumber?: string | null;
  headingStyle: React.CSSProperties;
  width: { width: string };
  phoneScreen: boolean;
  notAvailableText: string;
  testId?: string;
};

const PhoneEntry = ({
  label,
  phoneNumber,
  headingStyle,
  width,
  phoneScreen,
  notAvailableText,
  testId,
}: PhoneEntryProps) => (
  <PhoneAllContainer css={{ marginTop: '$sm' }}>
    <IconSymbolContainer color="$primary1" icon="call" size={18} />
    <CollapsePhoneContainer>
      <span style={headingStyle}>{label}</span>
      <CollapsePhoneNumberContainer style={width}>
        {phoneNumber ? (
          <a
            data-auto-testid={testId || phoneNumber}
            data-testid={testId || phoneNumber}
            href={`tel:${phoneNumber}`}
          >
            {getFormattedPhoneNumber(phoneNumber)}
          </a>
        ) : (
          <Text css={infoSubTextContainer(phoneScreen)}>
            {notAvailableText}
          </Text>
        )}
      </CollapsePhoneNumberContainer>
    </CollapsePhoneContainer>
  </PhoneAllContainer>
);

const AfterHoursLabel = ({
  label,
  headingStyle,
  shouldShowInfoIcon,
  phoneScreen,
  isWidget,
  onIconClick,
  popoverContent,
  isPopoverOpen,
  setIsPopoverOpen,
}: AfterHoursLabelProps) => {
  if (!shouldShowInfoIcon) {
    return <span style={headingStyle}>{label}</span>;
  }

  const iconStyle = { cursor: 'pointer', marginLeft: 4 };

  return (
    <span
      style={{
        ...headingStyle,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {label}
      {phoneScreen && !isWidget ? (
        <IconSymbol
          aria-label="About after hours"
          data-auto-testid="after-hours-info-icon"
          data-testid="after-hours-info-icon"
          icon="info"
          onClick={onIconClick}
          size={20}
          style={iconStyle}
          variant="outlined"
        />
      ) : isWidget ? (
        <AfterHoursPopoverIcon
          content={popoverContent}
          isOpen={isPopoverOpen}
          setIsOpen={setIsPopoverOpen}
        />
      ) : (
        <IconSymbol
          aria-label="About after hours"
          data-auto-testid="after-hours-info-icon"
          data-testid="after-hours-info-icon"
          icon="info"
          size={20}
          style={iconStyle}
          variant="outlined"
        />
      )}
    </span>
  );
};

const AfterHoursInfoDrawerComponent = ({
  isOpen,
  onClose,
  title,
  content,
}: AfterHoursInfoDrawerProps) => (
  <div data-testid="after-hours-info-drawer">
    <style>
      {`
        .after-hours-drawer .abyss-modal-base-body { background: #000000A8 !important; }
        .after-hours-drawer .abyss-modal-header-container { display: none !important; }
      `}
    </style>
    <Drawer
      className="after-hours-drawer"
      footer={
        <div
          data-testid="after-hours-info-drawer-footer"
          style={{
            padding: '0px 16px 48px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          <div
            style={{
              width: 'fit-content',
              marginLeft: 'auto',
              marginTop: 8,
            }}
          >
            <Button
              aria-label="Close"
              css={{
                'abyss-button-root': {
                  padding: 0,
                },
              }}
              data-testid="after-hours-drawer-close-btn"
              onClick={onClose}
              size="$sm"
              variant="text"
            >
              <IconSymbol
                data-auto-testid="icon-button-to-close"
                data-testid="icon-button-to-close"
                icon="close"
                size="24px"
              />
            </Button>
          </div>
          <div
            data-testid="after-hours-info-drawer-title"
            style={{
              color: '#002677',
              fontWeight: 700,
              fontSize: 29,
              marginBottom: 4,
              lineHeight: '36px',
            }}
          >
            {title}
          </div>
          <div
            data-testid="after-hours-info-drawer-content"
            style={{
              fontSize: 16,
              color: '#1A1A1A',
              fontWeight: 400,
            }}
          >
            {content}
          </div>
        </div>
      }
      hideClose={true}
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      size="sm"
    />
  </div>
);

export const CollapsePhones = ({
  selectedLocation,
  collapseHeader,
  phoneNumbers,
  providerDetails,
  tty,
}: Props) => {
  const { t } = useTranslation();
  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const phoneScreen = useMediaQuery(phoneOnly) || isWidget;
  const { coverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
    }))
  );
  const showAdditionalPhones =
    coverageType !== ReverseCoverageTypesCodes.VISION &&
    coverageType !== ReverseCoverageTypesCodes.DENTAL;
  const showExamPhone =
    coverageType === ReverseCoverageTypesCodes.VISION &&
    providerDetails?.providerType === ProviderType.PRACTITIONER;

  const { phone, appointment, emergency, fax } = phoneNumbers ?? {};
  const appointmentPhones = getAdditionalPhone(appointment);
  const primaryPhones = getPrimaryPhone(phone, appointment);
  const [showMorePhoneNumbers, setShowMorePhoneNumbers] =
    useState<boolean>(false);
  const [isAfterHoursDrawerOpen, setIsAfterHoursDrawerOpen] = useState(false);
  const [isAfterHoursPopoverOpen, setIsAfterHoursPopoverOpen] = useState(false);
  const {
    providerLocations: notAvailableOrUnknownText,
    headingStyle,
    isNotAvailableUnknownTextEnabled,
    descriptionStyle,
  } = useNotAvailableUnknownText();

  const width = {
    width: isNotAvailableUnknownTextEnabled ? '180px' : '149.5px',
  };

  const lagoonData = useLagoon('ui-messaging')();
  const featureFlags = useLagoon('feature-flags')();

  const afterHoursInfoDrawerTitle = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.ABOUT_AFTER_HOURS_TITLE,
  });
  const afterHoursInfoDrawerContent = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.ABOUT_AFTER_HOURS_CONTENT,
  });
  const [enableAfterHoursPhone, enableAllDirectoryPhoneNumbers] =
    getFeatureFlags(featureFlags, [
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_AFTER_HOURS_PHONE,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_ALL_DIRECTORY_PHONE_NUMBERS,
    ]);

  const currentMember = getCurrentMember();
  const memberSitusState = getMemberCoverageTypeSitusState(currentMember);
  const { isCnSMember } = currentMember;
  const isCNSNJPlanMember = isCnSMember && memberSitusState === 'NJ';
  const afterHoursObj = getPrimaryOrAfterHoursPhone(phoneNumbers);
  const afterHoursDisplayValue = enableAfterHoursPhone && afterHoursObj?.value;
  const isAfterHoursReal = afterHoursObj?.isAfterHours;
  const shouldShowInfoIcon =
    enableAfterHoursPhone && afterHoursObj && !isAfterHoursReal;

  return (
    <React.Fragment>
      <AfterHoursInfoDrawerComponent
        content={afterHoursInfoDrawerContent?.message}
        isOpen={isAfterHoursDrawerOpen}
        onClose={() => setIsAfterHoursDrawerOpen(false)}
        title={afterHoursInfoDrawerTitle?.message}
      />
      <div
        data-auto-testid="provider-locations-collapse-phones"
        data-testid="provider-locations-collapse-phones"
      >
        <SectionHeader justify="flex-end">
          <Heading
            css={h2SmallerFontForResponsive}
            data-auto-testid="contact-title"
            data-testid="contact-title"
            level={3}
            size="sm"
          >
            {t('PROVIDER_DETAILS.LOCATIONS_TAB.CONTACT')}
          </Heading>
        </SectionHeader>
        <PhoneAllContainer css={{ margin: '4px 0 0 0' }}>
          <IconSymbolContainer
            color="$core.color.brand.100"
            icon="call"
            size={18}
          />
          <CollapsePhoneContainer>
            <span style={headingStyle}>
              {t(
                enableAllDirectoryPhoneNumbers
                  ? 'PROVIDER_LOCATIONS_DETAILS.GENERAL_PHONE'
                  : 'PROVIDER_LOCATIONS_DETAILS.PRIMARY_PHONE'
              )}
            </span>

            <CollapsePhoneNumberContainer style={width}>
              {primaryPhones ? (
                <a
                  data-auto-testid="phone-primary"
                  data-testid="phone-primary"
                  href={`tel:${primaryPhones}`}
                >
                  {getFormattedPhoneNumber(primaryPhones)}
                </a>
              ) : (
                <Text css={infoSubTextContainer(phoneScreen)}>
                  {notAvailableOrUnknownText}
                </Text>
              )}
            </CollapsePhoneNumberContainer>
          </CollapsePhoneContainer>
        </PhoneAllContainer>

        {/* NEW: Feature flag enabled - Show simple appointment phone + View All link */}
        {enableAllDirectoryPhoneNumbers ? (
          <>
            {/* Appointment Phone - Always visible on mobile */}
            {showAdditionalPhones && (
              <PhoneEntry
                headingStyle={headingStyle}
                label={t('PROVIDER_LOCATIONS_DETAILS.APPOINTMENT_PHONE')}
                notAvailableText={notAvailableOrUnknownText}
                phoneNumber={appointmentPhones}
                phoneScreen={phoneScreen}
                width={width}
              />
            )}

            {/* View X Additional Numbers CTA - Opens drawer with all phone numbers */}
            <IconAlignmentContainer>
              <ViewAllPhonesLink
                enableAfterHoursPhone={enableAfterHoursPhone}
                isCNSNJPlanMember={isCNSNJPlanMember}
                isFromVirtualProviderGroup={false}
                mobile={true}
                phoneNumbers={phoneNumbers}
                showAdditionalPhones={showAdditionalPhones}
                showExamPhone={showExamPhone}
                tty={tty}
              />
            </IconAlignmentContainer>
          </>
        ) : (
          /* OLD: Feature flag disabled - Show full collapse with all phone types */

          <div>
            <CollapseProvider>
              <CollapsePhonesContainer
                aria-expanded={showMorePhoneNumbers}
                data-testid={collapseHeader}
                onClick={() => {
                  setShowMorePhoneNumbers(!showMorePhoneNumbers);
                }}
                role="button"
                tabIndex={0}
              >
                <CollapsePhonesHeader>{collapseHeader}</CollapsePhonesHeader>
                <ArrowContainer>
                  {showMorePhoneNumbers ? (
                    <IconSymbol icon="keyboard_arrow_up" size={20} />
                  ) : (
                    <IconSymbol icon="keyboard_arrow_down" size={20} />
                  )}
                </ArrowContainer>
              </CollapsePhonesContainer>
              <PhonesContainer cssProps={{ showMorePhoneNumbers }}>
                {showAdditionalPhones ? (
                  <div>
                    <IconAlignmentContainer css={{ marginTop: '13px' }}>
                      <PhoneAllContainer>
                        <IconSymbolContainer
                          color="$core.color.brand.100"
                          icon="call"
                          size={18}
                        />
                        <CollapsePhoneContainer>
                          <span style={headingStyle}>
                            {t('PROVIDER_LOCATIONS_DETAILS.APPOINTMENT_PHONE')}
                          </span>
                          <CollapsePhoneNumberContainer style={width}>
                            {appointmentPhones ? (
                              <a
                                data-auto-testid={appointmentPhones}
                                data-testid={appointmentPhones}
                                href={`tel:${appointmentPhones}`}
                              >
                                {getFormattedPhoneNumber(appointmentPhones)}
                              </a>
                            ) : (
                              <Text css={infoSubTextContainer(phoneScreen)}>
                                {notAvailableOrUnknownText}
                              </Text>
                            )}
                          </CollapsePhoneNumberContainer>
                        </CollapsePhoneContainer>
                      </PhoneAllContainer>
                    </IconAlignmentContainer>
                    <IconAlignmentContainer>
                      <PhoneAllContainer>
                        <IconSymbolContainer
                          color="$core.color.brand.100"
                          icon="call"
                          size={18}
                        />
                        <CollapsePhoneContainer>
                          <span style={headingStyle}>
                            {t('PROVIDER_LOCATIONS_DETAILS.EMERGENCY_PHONE')}
                          </span>
                          <CollapsePhoneNumberContainer style={width}>
                            {emergency ? (
                              <a
                                data-auto-testid={`emergency-phone-${emergency[0]}`}
                                data-testid={`emergency-phone-${emergency[0]}`}
                                href={`tel:${emergency[0]}`}
                              >
                                {getFormattedPhoneNumber(emergency[0])}
                              </a>
                            ) : (
                              <Text css={infoSubTextContainer(phoneScreen)}>
                                {notAvailableOrUnknownText}
                              </Text>
                            )}
                          </CollapsePhoneNumberContainer>
                        </CollapsePhoneContainer>
                      </PhoneAllContainer>
                    </IconAlignmentContainer>
                  </div>
                ) : null}
                {showExamPhone ? (
                  <IconAlignmentContainer>
                    <PhoneAllContainer>
                      <IconSymbolContainer
                        color="$core.color.brand.100"
                        icon="call"
                        size={18}
                      />
                      <CollapsePhoneContainer>
                        <span style={headingStyle}>
                          {t('OTHER_CARE_DETAILS.EXAM_PHONE')}
                        </span>
                        <CollapsePhoneNumberContainer style={width}>
                          {emergency ? (
                            <a
                              data-auto-testid={`emergency-phone-${emergency[0]}`}
                              data-testid={`emergency-phone-${emergency[0]}`}
                              href={`tel:${emergency[0]}`}
                            >
                              {getFormattedPhoneNumber(emergency[0])}
                            </a>
                          ) : (
                            <Text css={infoSubTextContainer(phoneScreen)}>
                              {notAvailableOrUnknownText}
                            </Text>
                          )}
                        </CollapsePhoneNumberContainer>
                      </CollapsePhoneContainer>
                    </PhoneAllContainer>
                  </IconAlignmentContainer>
                ) : null}
                {enableAfterHoursPhone && (
                  <IconAlignmentContainer>
                    <PhoneAllContainer>
                      <IconSymbolContainer
                        color="$core.color.brand.100"
                        icon="call"
                        size={18}
                      />
                      <CollapsePhoneContainer>
                        <AfterHoursLabel
                          headingStyle={headingStyle}
                          isPopoverOpen={isAfterHoursPopoverOpen}
                          isWidget={isWidget}
                          label={t(
                            'PROVIDER_LOCATIONS_DETAILS.AFTER_HOURS_PHONE'
                          )}
                          onIconClick={() => setIsAfterHoursDrawerOpen(true)}
                          phoneScreen={phoneScreen}
                          popoverContent={
                            afterHoursInfoDrawerContent?.message ?? ''
                          }
                          setIsPopoverOpen={setIsAfterHoursPopoverOpen}
                          shouldShowInfoIcon={shouldShowInfoIcon}
                        />
                        <CollapsePhoneNumberContainer style={width}>
                          {afterHoursDisplayValue ? (
                            <a
                              data-testid="after-hours-phone-provider-locations-tab"
                              href={`tel:${afterHoursDisplayValue}`}
                            >
                              {getFormattedPhoneNumber(afterHoursDisplayValue)}
                            </a>
                          ) : (
                            <Text css={infoSubTextContainer(phoneScreen)}>
                              {notAvailableOrUnknownText}
                            </Text>
                          )}
                        </CollapsePhoneNumberContainer>
                      </CollapsePhoneContainer>
                    </PhoneAllContainer>
                  </IconAlignmentContainer>
                )}
                <IconAlignmentContainer>
                  <PhoneAllContainer css={{ marginBottom: '0px' }}>
                    <IconSymbolContainer
                      color="$core.color.brand.100"
                      icon="call"
                      size={18}
                    />
                    <CollapsePhoneContainer>
                      <span style={headingStyle}>
                        {t('PROVIDER_LOCATIONS_DETAILS.TTY')}
                      </span>
                      <CollapsePhoneNumberContainer style={width}>
                        {tty ? (
                          <a
                            data-auto-testid={tty}
                            data-testid={tty}
                            href={`tel:${tty}`}
                          >
                            {tty}
                          </a>
                        ) : (
                          <Text css={infoSubTextContainer(phoneScreen)}>
                            {notAvailableOrUnknownText}
                          </Text>
                        )}
                      </CollapsePhoneNumberContainer>
                    </CollapsePhoneContainer>
                  </PhoneAllContainer>
                </IconAlignmentContainer>
                {isCNSNJPlanMember && (
                  <FaxSection
                    descriptionStyle={descriptionStyle}
                    fax={fax}
                    fontSize={{ fontSize: '16px' }}
                    headingStyle={headingStyle}
                    notAvailableOrUnknownText={notAvailableOrUnknownText}
                  />
                )}
              </PhonesContainer>
            </CollapseProvider>
          </div>
        )}
        {selectedLocation && (
          <EmailAndWebsiteInfo selectedLocation={selectedLocation} />
        )}
      </div>
    </React.Fragment>
  );
};
